import type { CSSProperties } from "react";
import { mapImageUrl, useMapImages } from "../api/queries";
import type { MapImageView } from "../api/types";
import { text } from "../text";

/** A polygon as points `[x, y]` in percent of the image's width and height, measured from the top left. */
export type MapPolygon = number[][];

/** The uploaded image of a map, or undefined while loading and when no image has been uploaded. */
export function useMapImage(system: string, map: string | undefined): MapImageView | undefined {
  const maps = useMapImages(system);
  return maps.data?.find((m) => m.id === map && m.uploaded);
}

export function polygonPoints(polygon: MapPolygon): string {
  return polygon.map(([x, y]) => `${x},${y}`).join(" ");
}

/**
 * Shows an uploaded map image with highlighted areas on top. Renders nothing without an image.
 * `context` are outlines drawn faintly below the highlight, such as the surroundings the highlighted areas belong to.
 */
export function MapView(props: { system: string; map: string | undefined; areas: MapPolygon[]; context?: MapPolygon[]; label: string; className?: string }) {
  const image = useMapImage(props.system, props.map);
  if (!image) return null;
  return (
    <figure
      className={`map-view${props.className ? ` ${props.className}` : ""}`}
      aria-label={props.label}
      style={{ "--map-aspect": image.width / image.height } as CSSProperties}
    >
      <div className="map-frame" style={{ aspectRatio: `${image.width} / ${image.height}` }}>
        <img src={mapImageUrl(props.system, image)} alt={text(image.name)} draggable={false} />
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {props.context?.map((polygon, index) => (
            <polygon key={`context-${index}`} className="map-area context" points={polygonPoints(polygon)} />
          ))}
          {props.areas.map((polygon, index) => (
            <polygon key={index} className="map-area" points={polygonPoints(polygon)} />
          ))}
        </svg>
      </div>
    </figure>
  );
}
